// The Configure tab: one form, one Save.
//
// It was three cards with three Save buttons — General and Filters both
// PATCHing /v1/screens/{id}, Tuning PATCHing that row a second time through
// /v1/screens/{id}/tuning. Nothing on the page could answer "does this button
// cover the change I just made in the section above it", and a visitor who
// edited two sections and saved one lost the other silently.
//
// So: one RHF form over all three sections, and kore's SaveBar, which is the
// design system's locked treatment for exactly this (five states, ⌘S, a
// floating pill). The server side of the same change let one PATCH carry the
// tuning knobs, so this is one request, not a fan-out that could half-fail.
//
// The offset is not here. It belongs to the Operate tab's Quick offset, which
// nudges it against its own endpoint — and a Configure save carrying it would
// revert any nudge made since the form was opened.

import { SaveBar } from "@pej/kore/components";
import { ApiError } from "@/api/error";
import { usePatchScreen } from "@/api/screens";
import type { Screen, Template } from "@/api/types";
import { toastMutationError } from "@/components/common/toast-mutation-error";
import { applyProblemDetails, Form, firstFieldMessage, useKoreForm } from "@/lib/forms";
import { friendlyMessage } from "@/lib/friendly-error";
import { SAVE_BAR_IN_FLOW, useSaveBar } from "@/lib/save-bar";
import { type ConfigureOutput, configureSchema, toBody, toDefaults } from "./configure-schema";
import { FiltersSection, GeneralSection, TuningSection } from "./configure-sections";

export interface ScreenConfigureFormProps {
    workspaceId: string;
    screen: Screen;
    templates: Template[];
}

export function ScreenConfigureForm({ workspaceId, screen, templates }: ScreenConfigureFormProps) {
    const patchScreen = usePatchScreen(screen.locationId, screen.id);
    const form = useKoreForm({ schema: configureSchema, defaultValues: toDefaults(screen) });
    const saveBar = useSaveBar({
        dirty: form.formState.isDirty,
        saving: patchScreen.isPending,
        invalid: Object.keys(form.formState.errors).length > 0,
    });

    async function onSubmit(values: ConfigureOutput) {
        saveBar.submitting();
        try {
            const updated = await patchScreen.mutateAsync(toBody(values));
            form.reset(toDefaults(updated));
            saveBar.saved();
        } catch (err) {
            if (err instanceof ApiError) {
                // Field-level violations land on their fields and the bar names
                // them. Anything else is the server's to explain, in a toast:
                // the bar only says the save failed, never the raw message.
                const { onFields, unmapped } = applyProblemDetails(form, err);
                if (onFields) {
                    saveBar.failed(friendlyMessage(err));
                    return;
                }
                toastMutationError("Save failed", err, { unmapped });
                saveBar.failed("Couldn't save this screen. Try again.");
                return;
            }
            throw err;
        }
    }

    return (
        <Form
            form={form}
            onSubmit={onSubmit}
            // `spacing="none"`: kore's Form adds `space-y-6` of its own, which
            // stacked on top of a flex gap here and made every gap on this page
            // 40px instead of one rhythm.
            spacing="none"
            className="flex max-w-4xl flex-col gap-4"
        >
            <GeneralSection
                control={form.control}
                workspaceId={workspaceId}
                templates={templates}
                templateId={String(form.watch("templateId"))}
            />
            <FiltersSection control={form.control} />
            <TuningSection control={form.control} />
            {/* The bar's buttons are Base UI buttons, which default to
                type="button" — so Save routes through handleSubmit here rather
                than submitting the form natively. Not gated on validity: a
                disabled Save that cannot say why is worse than one that
                submits and names the field that is wrong. */}
            <SaveBar
                className={SAVE_BAR_IN_FLOW}
                state={saveBar.state}
                errorMessage={saveBar.errorMessage}
                // No count. RHF counts a whole field array as one dirty field,
                // so three edited filter rows would report "1 unsaved change".
                statusLabels={{ dirty: () => "Unsaved changes" }}
                // The invalid path matters as much as the valid one: this bar
                // can sit a screen below the field it stopped on, so it says
                // which field that was. RHF focuses it at the same time.
                onSave={() =>
                    void form.handleSubmit(onSubmit, (errors) => saveBar.blocked(firstFieldMessage(errors)))()
                }
                // The action never changes name. "Retry" would be wrong for a
                // field that needs fixing rather than a request to repeat.
                retryLabel="Save changes"
                // No toast: the fields visibly revert and the bar goes clean.
                onDiscard={() => form.reset(toDefaults(screen))}
            />
        </Form>
    );
}
